import { ViewportScroller } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { AfterViewChecked, AfterViewInit, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { Entry } from 'contentful';
import { APIReferenceDetailXMLSection } from 'src/app/model/section/api-references-details-xml-section';
import { APIReferenceResponsePage } from 'src/app/model/shared/api-reference-response-page';
import { ContentfulService } from 'src/app/service/contentful.service';
import { PageVersionService } from 'src/app/service/page-version.service';
import { SharedDataService } from 'src/app/service/shared-data.service';
import { ModelValidator } from 'src/app/validator/model-validator';
import htmlTitle from 'src/assets/json/redirects/html-title.json';

@Component({
  selector: 'app-api-reference-response-page',
  templateUrl: './api-reference-response-page.component.html',
  styleUrls: ['./api-reference-response-page.component.scss']
})
export class APIReferenceResponsePageComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
  @Input() pageName: string = '';
  @Input() href: string = '';

  pageVersions: string[] = [];
  pageVersionServiceSubscription: any;
  apiReferenceResponsePage: APIReferenceResponsePage = {} as APIReferenceResponsePage;
  apiReferenceDetailsXMLSectionMap = new Map();

  alreadyScrolled: boolean = false;

  constructor(
    private scroller: ViewportScroller,
    private contentfulService: ContentfulService,
    private sharedDataService: SharedDataService,
    private pageVersionService: PageVersionService,
    private httpClient: HttpClient
  ) {}

  ngAfterViewChecked() {
    this.scrollToHref(this.href);
  }

  scrollToHref(value: string) {
    if (value && value.length > 0 && !this.alreadyScrolled) {
      this.scroller.scrollToAnchor(value);
    }
    // console.log('alreadyScrolled : ', this.alreadyScrolled);
    // console.log('scrollToHref : ' + value);
  }

  ngAfterViewInit() {
    setTimeout(() => {
      this.alreadyScrolled = true;
    }, 2000);
  }

  ngOnInit(): void {
    htmlTitle.filter((title) => {
      if (title.page === 'api-reference-responses') {
        this.apiReferenceResponsePage.htmlTitle = title.htmlTitle;
      }
    });
    this.apiReferenceResponsePage.noteSections = [];
    const versionQuery = { 'fields.title': this.pageName, select: 'sys.id,fields.version' };

    this.contentfulService.getPageVersions(versionQuery).then((apiReferenceResponsePageItems) => {
      this.pageVersions = apiReferenceResponsePageItems
        .filter((item: { fields: undefined }) => item.fields !== undefined)
        .map((item: { fields: { version: string } }) => item.fields.version)
        .sort();

      let selectedPageVersion = '';
      if (this.pageVersions.length > 0) {
        selectedPageVersion = this.pageVersions[0];
        // console.log(this.pageName + ' >>>> default page version : ' + selectedPageVersion);
      }

      // console.log('default selectedPageVersion : ', selectedPageVersion);
      this.getPageContentFromContentful(selectedPageVersion);
    });

    this.pageVersionServiceSubscription = this.pageVersionService.pageVersion.subscribe((pageVersionData) => {
      if (pageVersionData.pageName === this.pageName && pageVersionData.selectedPageVersion !== undefined && pageVersionData.selectedPageVersion !== '') {
        // console.log('pageVersionData : ', pageVersionData);
        this.href = '';
        this.getPageContentFromContentful(pageVersionData.selectedPageVersion);
      }
    });
  }

  getPageContentFromContentful(selectedPageVersion: string) {
    // console.log(this.pageName + ' >>>> select page version : ' + selectedPageVersion);

    const query = selectedPageVersion === '' ? { 'fields.title': this.pageName, include: 2 } : { 'fields.title': this.pageName, 'fields.version': selectedPageVersion, include: 2 };
    this.contentfulService.getPageContentResponse(query).then((apiReferencePageResponse) => {
      this.apiReferenceDetailsXMLSectionMap = new Map();
      this.apiReferenceResponsePage.noteSections = [];
      this.apiReferenceResponsePage.schemaDataModelAccordianSections = [];
      this.apiReferenceResponsePage.apiReferenceDetailXMLSections = [];

      this.mapBanner(apiReferencePageResponse.includes.Entry);
      this.apiReferenceResponsePage.banner.pageName = this.pageName;
      this.apiReferenceResponsePage.banner.pageVersions = this.pageVersions;
      this.apiReferenceResponsePage.banner.defaultPageVersion = selectedPageVersion;
      this.sharedDataService.addSharedData(this.apiReferenceResponsePage.banner);
      // console.log(this.pageName + ' >>>> >>>> banner.pageVersions : ' + this.apiReferenceResponsePage.banner.pageVersions);
      this.mapPageInfo(apiReferencePageResponse.items);
      this.mapSection(apiReferencePageResponse.includes.Entry);
      this.mapNoteSection(apiReferencePageResponse.includes.Entry);
      this.mapSubSectionToSection();

      this.apiReferenceResponsePage.apiReferenceDetailXMLSections.forEach((parentSection) => {
        if (parentSection.subSections) {
          parentSection.subSections.sort((a, b) => a.order - b.order);
        }
      });

      // console.log('parent sections with sub sections : ', this.apiReferenceResponsePage.apiReferenceDetailXMLSections);
    });
  }

  private mapBanner(apiReferenceResponsePageEntries: Entry<any>[]) {
    apiReferenceResponsePageEntries
      .filter((entry) => ModelValidator.isBanner(entry))
      .forEach((entry) => {
        this.mapEntryToBanner(entry);
      });
  }

  private mapEntryToBanner(entry: Entry<any>) {

    let imageDetails: any = entry.fields['image'];
    let videoDetails: any = entry.fields['video'];

    this.apiReferenceResponsePage.banner = {
      title: entry.fields['title'] as string,
      subTitle: entry.fields['subTitle'] as string,
      image: imageDetails.fields.file.url,
      video: videoDetails?.fields?.file?.url
    };
  }

  private mapPageInfo(items: Entry<any>[]) {
    items.forEach((entry: any) => {
      let editorDetails = entry.fields?.editorDetails?.split('#');
      this.apiReferenceResponsePage.banner.lastUpdated = entry.fields?.lastUpdated;
      this.apiReferenceResponsePage.banner.url = entry.fields?.url;
      this.apiReferenceResponsePage.banner.editorEmail = editorDetails?.pop();
      this.apiReferenceResponsePage.banner.editorShortName = editorDetails?.pop();
      this.apiReferenceResponsePage.banner.editorFullName = editorDetails?.pop();
      this.apiReferenceResponsePage.htmlTitle = entry.fields.htmlTitle;
    });
  }

  private mapSection(apiReferenceResponsePageEntries: Entry<any>[]) {
    apiReferenceResponsePageEntries
      .filter((entry) => ModelValidator.isSection(entry) || ModelValidator.isTableSection(entry))
      .forEach((entry) => {
        if (entry.fields['xml'] === undefined && ModelValidator.isSection(entry)) {
          this.mapNonXMLSectionToEntries(entry);
        } else {

          let pageTitle=entry.fields['title'] as string;
          let urlID = encodeURI(this.pageName + '_' + pageTitle.split('#').pop());
          let apiReferenceDetailXMLSection: APIReferenceDetailXMLSection = {
            order: entry.fields['order'] as number,
            title: urlID,
            fullTitle: entry.fields['group'] as string,
            group: entry.fields['group'] as string,
            category: entry.fields['category'] as string,
            body: entry.fields['body'] as string,
            subBody: '',
            tableSections: [],
            heading: entry.fields['heading'] as string,
            sectionClass: entry.fields['tableClass'] as string
          };
          if (typeof entry.fields['xml'] !== 'undefined' && ModelValidator.isSection(entry)) {

            let givenBody:any=entry.fields['xml'];
            let pageBodyTitle=givenBody?.content[0]?.data?.target?.fields?.title as string;

            apiReferenceDetailXMLSection = {
              order: entry.fields['order'] as number,
              title: urlID,
              heading: entry.fields['heading'] as string,
              fullTitle: entry.fields['title'] as string,
              group: entry.fields['group'] as string,
              category: entry.fields['category'] as string,
              body: entry.fields['body'] as string,
              subBody: pageBodyTitle.split('#').pop(),
              subSections: [],
              tableSections: [],
              xmlDataArray: [],
              sectionClass: entry.fields['sectionClass'] as string 
            };
          }

          if (apiReferenceDetailXMLSection.category !== 'sub-section') {
            this.mapTableSection(apiReferenceDetailXMLSection, apiReferenceResponsePageEntries);
          }

          if (apiReferenceDetailXMLSection.body !== undefined || apiReferenceDetailXMLSection.category === 'sub-section') {
            this.mapXMLSectionToEntries(apiReferenceDetailXMLSection, entry.fields['xml']);
          }
        }
      });
  }

  private mapNonXMLSectionToEntries(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceResponsePage.schemaDataModelAccordianSections.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      subBody: entry.fields['subBody'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      heading: entry.fields['heading'] as string
    });
    this.apiReferenceResponsePage.schemaDataModelAccordianSections.sort((a, b) => a.order - b.order);
  }

  private mapTableSection(apiReferenceDetailXMLSection: APIReferenceDetailXMLSection, orderRetrievePageEntries: Entry<any>[]) {
    orderRetrievePageEntries
      .filter((entry) => ModelValidator.isTableSection(entry) && apiReferenceDetailXMLSection.fullTitle === entry.fields['group'])
      .forEach((entry) => {
        this.mapTableSectionToDetailsXMLSections(apiReferenceDetailXMLSection, entry);
      });
  }

  private mapTableSectionToDetailsXMLSections(apiReferenceDetailXMLSection: APIReferenceDetailXMLSection, entry: Entry<any>) {
    

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();
    
    apiReferenceDetailXMLSection.tableSections?.push({
      title: pageTitle,
      body: entry.fields['body'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      table: entry.fields['table'] as string,
      tableClass: entry.fields['tableClass'] as string
    });
  }

  private mapXMLSectionToEntries(apiReferenceDetailsXMLSection: APIReferenceDetailXMLSection, xmlFiles: any) {
    // console.log('apiReferenceDetailsXMLSection : ', apiReferenceDetailsXMLSection);
    // console.log('>>>> xml files : ', xmlFiles);

    let xmlDataArray: any[] = [];
    xmlFiles?.content
      .filter((xmlFileContent: { nodeType: string }) => xmlFileContent.nodeType === 'embedded-asset-block')
      .forEach((element: any) => {
        let xmlTitle = element?.data?.target?.fields?.title?.split('#').pop();
        this.httpClient.get(element?.data?.target?.fields?.file.url, { responseType: 'text' }).subscribe({
          next: (data) => xmlDataArray.push({ data: data, title: xmlTitle }),
          error: (cause) => xmlDataArray.push({ data: cause.message, title: xmlTitle })
        });
      });

    this.mapXMLSectionToEntriesWithXMLData(apiReferenceDetailsXMLSection, xmlDataArray);
  }

  private mapXMLSectionToEntriesWithXMLData(apiReferenceDetailsXMLSection: APIReferenceDetailXMLSection, xmlDataArray: any[]) {
    apiReferenceDetailsXMLSection.xmlDataArray = xmlDataArray;
    this.apiReferenceDetailsXMLSectionMap.set(apiReferenceDetailsXMLSection.heading + '##' + apiReferenceDetailsXMLSection.group, apiReferenceDetailsXMLSection);
    // console.log('apiReferenceDetailsXMLSectionMap : ', this.apiReferenceDetailsXMLSectionMap);
    this.apiReferenceResponsePage.apiReferenceDetailXMLSections = [...this.apiReferenceDetailsXMLSectionMap.values()]
      .filter((section) => section.category !== 'sub-section')
      .sort((a, b) => a.order - b.order);

    // console.log('parent sections : ', this.apiReferenceResponsePage.apiReferenceDetailXMLSections);
  }

  private mapSubSectionToSection() {
    [...this.apiReferenceDetailsXMLSectionMap.values()]
      .filter((section) => section.category === 'sub-section')
      .forEach((subSection) => {
        this.apiReferenceResponsePage.apiReferenceDetailXMLSections.forEach((parentSection) => {
          if (parentSection.fullTitle === subSection.group) {
            parentSection.subSections?.push(subSection);
          }
        });
      });
  }

  private mapNoteSection(res: Entry<any>[]) {
    res
      .filter((entry) => ModelValidator.isNote(entry) && entry.fields['category'] === undefined)
      .forEach((entry) => {
        this.mapEntryToNoteSection(entry);
      });
  }

  private mapEntryToNoteSection(entry: Entry<any>) {

    let givenTitle:any=entry.fields['title'] as string;
    let pageTitle=givenTitle.split('#').pop();

    this.apiReferenceResponsePage.noteSections.push({
      fullTitle: entry.fields['title'] as string,
      title: pageTitle,
      body: entry.fields['body'] as string,
      iconClass: entry.fields['iconClass'] as string,
      group: entry.fields['group'] as string,
      order: entry.fields['order'] as number,
      noteClass: entry.fields['noteClass'] as string
    });
  }

  ngOnDestroy() {
    this.pageVersionServiceSubscription.unsubscribe();
  }
}
